import React from 'react';
import { Card } from './ui/card';
import { Coffee, Waves, Anchor, Mountain, UtensilsCrossed, Palmtree, Castle, Landmark, Building, Ship, Cross, Columns } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mapImage } from '../utils/imageLibrary';

export const LocationHighlights = () => {
  const navigate = useNavigate();
  const highlights = [
    {
      icon: Castle,
      title: 'Sintra UNESCO Site',
      description: 'Fairy-tale palaces and medieval castles',
      distance: '15 min drive',
      isSpecial: true,
      link: '/sintra'
    },
    {
      icon: Landmark,
      title: 'Lisboa - The Capital',
      description: 'Portugal\'s vibrant historic capital city',
      distance: '30 min train',
      isSpecial: true,
      link: '/lisbon'
    },
    {
      icon: Building,
      title: 'Óbidos Medieval Town',
      description: 'Walled town with cobblestone streets & Ginjinha',
      distance: '1 hour drive',
      isSpecial: true,
      link: '/obidos'
    },
    {
      icon: Waves,
      title: 'Nazaré Big Waves',
      description: 'World-record surfing & dramatic coastline',
      distance: '1 hour drive',
      isSpecial: true,
      link: '/nazare'
    },
    {
      icon: Ship,
      title: 'Peniche & Berlengas',
      description: 'Surfing capital & island nature reserve',
      distance: '2 hour drive',
      isSpecial: true,
      link: '/peniche'
    },
    {
      icon: Cross,
      title: 'Tomar Templar City',
      description: 'Knights Templar headquarters & UNESCO site',
      distance: '2 hour drive',
      isSpecial: true,
      link: '/tomar'
    },
    {
      icon: Columns,
      title: 'Évora Roman City',
      description: '2000+ years of history & Roman temple',
      distance: '2 hour drive',
      isSpecial: true,
      link: '/evora'
    },
    {
      icon: Waves,
      title: 'Beach Paradise',
      description: 'Multiple pristine beaches within 5 minutes walk',
      distance: '5 min walk',
      link: '/cascais'
    },
    {
      icon: Anchor,
      title: 'Cascais Marina',
      description: 'Waterfront dining and luxury yachts',
      distance: '7 min walk',
      link: '/cascais'
    },
    {
      icon: Mountain,
      title: 'Boca do Inferno',
      description: 'Dramatic cliffs and natural wonder',
      distance: '20 min walk',
      link: '/cascais'
    },
    {
      icon: UtensilsCrossed,
      title: 'World-Class Dining',
      description: 'Dozens of restaurants steps from your door',
      distance: '2 min walk'
    },
    {
      icon: Coffee,
      title: 'Yellow Streets',
      description: 'Iconic Cascais charm and café culture',
      distance: 'On location',
      link: '/cascais'
    }
  ];
  
  return (
    <section className="py-16 sm:py-20 px-4 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        {/* Compact Section Header */}
        <div className="max-w-3xl mb-10">
          <h2 className="text-3xl sm:text-4xl font-display font-semibold text-foreground mb-3">
            The Perfect Location
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed">
            Welcome to the heart of downtown Cascais. Everything you need is within walking distance. Amazing shops, grocery stores, eateries, bars and cafes, beaches, palaces and so much more. No car is necessary, but if you need one, rentals are available around the corner, or take the coastal train into Lisbon, or a $15 Uber to Sintra!
          </p>
        </div>
        
        {/* Compact Highlights Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {highlights.map((highlight, index) => {
            const Icon = highlight.icon;
            const isSpecial = highlight.isSpecial;
            const hasLink = highlight.link;
            return (
              <Card 
                key={index}
                className={`p-4 bg-card transition-all duration-300 group ${
                  isSpecial 
                    ? 'border-primary/50 hover:border-primary cursor-pointer hover:shadow-lg' 
                    : hasLink
                      ? 'border-border hover:border-primary/50 cursor-pointer hover:shadow-md'
                      : 'border-border hover:border-primary/50'
                }`}
                onClick={hasLink ? () => navigate(highlight.link) : undefined}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className={`p-2 rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none transition-all duration-300 ${
                      isSpecial
                        ? 'bg-primary/15 text-primary group-hover:bg-primary group-hover:text-primary-foreground'
                        : 'bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground'
                    }`}>
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-display font-semibold text-foreground mb-0.5">
                      {highlight.title}
                    </h3>
                    {hasLink && <div className="text-xs text-primary mb-1.5">Learn More →</div>}
                    <p className="text-xs text-muted-foreground mb-1.5 leading-relaxed">
                      {highlight.description}
                    </p>
                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none text-xs font-medium ${
                      isSpecial 
                        ? 'bg-primary/10 text-primary' 
                        : hasLink
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted/50 text-muted-foreground'
                    }`}>
                      {highlight.distance}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
        
        {/* OpenStreetMap */}
        <div className="mt-12">
          <Card className="overflow-hidden border-border shadow-lg hover:shadow-xl transition-all duration-300">
            {/* Map Header */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 px-6 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none bg-primary/15 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-display font-semibold text-foreground">
                    Find Us in Cascais
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your Urban Oasis awaits
                  </p>
                </div>
              </div>
            </div>
            
            {/* Map Container - Static Map Image */}
            <div className="relative overflow-hidden">
              <div className="aspect-[21/9] w-full">
                <img
                  src={mapImage.url}
                  alt={mapImage.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              {/* Subtle overlay gradient at edges */}
              <div className="absolute inset-0 pointer-events-none border border-border/50 rounded-none"></div>
            </div>
            
            {/* Map Footer */}
            <div className="px-6 py-5 bg-card border-t border-border">
              <div className="flex items-start gap-4">
                <div className="hidden sm:block p-3 rounded-tl-xl rounded-br-xl rounded-tr-none rounded-bl-none bg-muted/50">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </div>
                <div>
                  <h4 className="text-base font-display font-semibold text-foreground mb-1">
                    Rua Dom Francisco de Avilez
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Downtown Cascais, Portugal • Heart of the Yellow Streets
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

const MapPin = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);